import React, { useState, useEffect, useMemo } from 'react';
import { getCustomerSpares, takeCustomerSpare, fileUrl } from '../../services/api';
import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { Package, Search, Warehouse, MapPin, Minus, Hash, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const CustomerSpares = () => {
  const [data, setData] = useState({ stores: [], parts: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [storeFilter, setStoreFilter] = useState('all');

  // Take dialog state
  const [takeRow, setTakeRow] = useState(null); // { part, stockRow }
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const refresh = () => getCustomerSpares().then(setData);
  useEffect(() => { refresh().finally(() => setLoading(false)); }, []);

  const filteredParts = useMemo(() => {
    let list = data.parts || [];
    if (storeFilter !== 'all') {
      list = list
        .map(b => ({
          ...b,
          stock: b.stock.filter(s => s.storeId === storeFilter),
        }))
        .filter(b => b.stock.length > 0);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(b =>
        (b.part.name || '').toLowerCase().includes(q) ||
        (b.part.sku || '').toLowerCase().includes(q) ||
        (b.part.description || '').toLowerCase().includes(q) ||
        (b.part.barcode || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [data.parts, storeFilter, search]);

  const openTake = (part, stockRow) => {
    setTakeRow({ part, stockRow });
    setQty(1);
    setNotes('');
  };

  const submitTake = async () => {
    if (!takeRow) return;
    const max = takeRow.stockRow.quantity;
    const n = parseInt(qty, 10);
    if (!Number.isFinite(n) || n <= 0) return toast.error('Enter a valid quantity');
    if (n > max) return toast.error(`Only ${max} available`);
    setSubmitting(true);
    try {
      await takeCustomerSpare(takeRow.part.id, {
        storeId: takeRow.stockRow.storeId,
        locationId: takeRow.stockRow.locationId,
        qty: n,
        notes: notes.trim(),
      });
      toast.success(`Took ${n} × ${takeRow.part.name}`);
      setTakeRow(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to take part');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-8 text-slate-500">Loading spares...</div>;

  const noStores = !data.stores || data.stores.length === 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageHeader
        title="Spares Stock"
        description="Pull spare parts from your own on-site stores. Every pickup is logged for your records."
      />

      {noStores && (
        <div
          data-testid="no-stores-empty"
          className="p-12 text-center text-slate-500 bg-white border border-dashed border-slate-200 rounded-md"
        >
          <Warehouse className="h-10 w-10 mx-auto mb-3 text-slate-400" />
          <div className="font-medium text-slate-700 mb-1">No on-site store yet</div>
          <div className="text-sm">
            Once Lutrom sets up your spares store, your stock will appear here.
          </div>
        </div>
      )}

      {!noStores && (
        <>
          {/* Store filter chips + search */}
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <button
              type="button"
              onClick={() => setStoreFilter('all')}
              data-testid="store-filter-all"
              className={`px-3 py-1.5 rounded-md font-medium transition-colors ${
                storeFilter === 'all'
                  ? 'bg-slate-900 text-white'
                  : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
              }`}
            >
              All stores
              <span className="text-xs opacity-70 ml-1">({data.stores.length})</span>
            </button>
            {data.stores.map(s => (
              <button
                key={s.id}
                type="button"
                onClick={() => setStoreFilter(s.id)}
                data-testid={`store-filter-${s.id}`}
                className={`px-3 py-1.5 rounded-md font-medium transition-colors flex items-center gap-1.5 ${
                  storeFilter === s.id
                    ? 'bg-slate-900 text-white'
                    : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
                }`}
              >
                <Warehouse className="h-3.5 w-3.5" /> {s.name}
              </button>
            ))}
            <div className="relative flex-1 min-w-[240px] ml-auto">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="spares-search-input"
                placeholder="Search by part name, SKU, barcode..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 bg-white"
              />
            </div>
          </div>

          {filteredParts.length === 0 && (
            <div
              data-testid="no-spares-empty"
              className="p-12 text-center text-slate-500 bg-white border border-dashed border-slate-200 rounded-md"
            >
              {(data.parts || []).length === 0
                ? 'Your spares store is currently empty.'
                : 'No parts match your filter.'}
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-4">
            {filteredParts.map(({ part, totalStock, stock }) => (
              <div
                key={part.id}
                data-testid={`spare-card-${part.id}`}
                className="bg-white border border-slate-200 rounded-md p-5 hover:border-blue-700 transition-colors"
              >
                <div className="flex items-start justify-between gap-3 mb-4">
                  {part.photoUrl && (
                    <img
                      src={fileUrl(part.photoUrl)}
                      alt={part.name}
                      className="h-16 w-16 rounded object-cover border border-slate-200 shrink-0"
                      data-testid={`spare-photo-${part.id}`}
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-xs text-slate-500 font-mono">
                      {part.sku && <span>{part.sku}</span>}
                      {part.barcode && <span className="flex items-center gap-1"><Hash className="h-3 w-3" />{part.barcode}</span>}
                    </div>
                    <h3 className="font-display uppercase text-base text-slate-900 tracking-wide mt-1 truncate">
                      {part.name}
                    </h3>
                    {part.description && (
                      <p className="text-xs text-slate-500 mt-1 line-clamp-2">{part.description}</p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs uppercase tracking-wider text-slate-500">In stock</div>
                    <div className="text-2xl font-display text-slate-900">{totalStock}</div>
                  </div>
                </div>

                <div className="space-y-1.5 pt-3 border-t border-slate-100">
                  {stock.map((row) => (
                    <div
                      key={row.id}
                      className="flex items-center justify-between gap-3 text-sm py-1"
                      data-testid={`spare-row-${part.id}-${row.id}`}
                    >
                      <div className="min-w-0 flex items-center gap-2 text-slate-700">
                        <Warehouse className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                        <span className="font-medium truncate">{row.storeName}</span>
                        {row.locationName && (
                          <>
                            <MapPin className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                            <span className="text-slate-500 truncate">{row.locationName}</span>
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="text-slate-600 tabular-nums">
                          {row.quantity} <span className="text-xs text-slate-400">on hand</span>
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openTake(part, row)}
                          disabled={row.quantity <= 0}
                          data-testid={`take-spare-${part.id}-${row.id}`}
                          className="h-8 text-xs"
                        >
                          <Minus className="h-3.5 w-3.5 mr-1" /> Take
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {(!part.totalStock || totalStock === 0) ? null : null}
                {totalStock > 0 && totalStock <= 1 && (
                  <div className="mt-3 flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 rounded px-2 py-1">
                    <AlertCircle className="h-3.5 w-3.5" /> Low stock — let us know if you need more.
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Take dialog */}
      <Dialog open={!!takeRow} onOpenChange={(o) => !o && setTakeRow(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Take from stock</DialogTitle>
            <DialogDescription>
              {takeRow && (
                <>
                  <span className="font-semibold text-slate-900">{takeRow.part.name}</span>
                  {' · '}
                  {takeRow.stockRow.storeName}
                  {takeRow.stockRow.locationName ? ` · ${takeRow.stockRow.locationName}` : ''}
                  {' · '}
                  <span className="font-mono">{takeRow.stockRow.quantity} on hand</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Quantity to take *</Label>
              <Input
                className="mt-1.5"
                type="number"
                min="1"
                max={takeRow?.stockRow.quantity ?? 1}
                value={qty}
                onChange={e => setQty(e.target.value)}
                data-testid="take-qty-input"
              />
              {takeRow && (
                <p className="text-xs text-slate-500 mt-1">Max {takeRow.stockRow.quantity}.</p>
              )}
            </div>
            <div>
              <Label>What's it for? (optional)</Label>
              <Textarea
                className="mt-1.5"
                rows={3}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="e.g. Replacing belt on Conveyor A"
                data-testid="take-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTakeRow(null)} disabled={submitting}>Cancel</Button>
            <Button
              onClick={submitTake}
              disabled={submitting}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="confirm-take-btn"
            >
              {submitting ? 'Taking...' : 'Confirm pickup'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerSpares;
